// import type {InputProps as StandardInputsProps} from '@mui/material/Input';
import type {BaseTextFieldProps as MuiBaseTextFieldProps} from '@mui/material/TextField';
import React, {forwardRef} from 'react';
import type {FormControlProps as DefaultFormControlProps} from '../Form/FormControl';
import {FormControl} from '../Form/FormControl';
import {FormHelperText} from '../Form/FormHelperText';
import {useId} from '../hooks';
import type {InputProps} from '../Input/Input';
import {Input} from '../Input/Input';
import {InputLabel} from '../Input/InputLabel';

type ModifiedTextFieldProps = Omit<MuiBaseTextFieldProps, 'color' | 'size' | 'select' | 'SelectProps' | 'margin' | 'onKeyDown' | 'onKeyUp' | 'variant' | 'type' | 'classes'>;

type ModifiedInputProps = Omit<InputProps, 'ref'>;

export interface BaseTextFieldProps extends ModifiedTextFieldProps, ModifiedInputProps {}

export interface TextFieldProps extends BaseTextFieldProps {
    /**
      * IDs are automatically generated by component for accessibility purposes.
      * Use this prop to pass a specific ID value that will be appended.
      * @default "";
      *
      */
    id?: string;
    /**
     * Triggered event when keyboard "Enter" is pressed;
     */
    onEnter?: React.KeyboardEventHandler<HTMLInputElement>;
    /**
     * Exposing FormControl API;
     * @default {}
     */
    FormControlProps?: DefaultFormControlProps;
}

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(({
    label, FormControlProps, InputLabelProps, required, fullWidth = true, sx, id = '',
    name, hiddenLabel, inputRef, inputProps, helperText, onEnter, onKeyPress, error,
    disabled, ...props
}, ref) => {
    const generatedId = useId(id, 'textField');

    // Enable trigger for 'pressing' enter
    const handleKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        // Run Enter function if available
        if (e.key === 'Enter') {
            if (onEnter && typeof onEnter === 'function') {
                onEnter(e);
            }
        }

        // Run default onKeyPress, if available
        if (onKeyPress && typeof onKeyPress === 'function') {
            onKeyPress(e);
        }
    };

    return (
        <FormControl
            disabled={disabled}
            fullWidth={fullWidth}
            required={required}
            sx={sx}
            {...FormControlProps}
        >
            {label &&
                <InputLabel
                    {...InputLabelProps}
                    disabled={disabled}
                    hiddenLabel={hiddenLabel}
                    htmlFor={generatedId}
                >{label}</InputLabel>
            }
            <Input
                {...props}
                disabled={disabled}
                error={error}
                fullWidth
                id={generatedId}
                inputProps={inputProps}
                inputRef={inputRef}
                name={name || generatedId}
                onKeyPress={handleKeyPress}
                ref={ref}
            />
            {helperText && (
                <FormHelperText
                    disabled={disabled}
                    error={error}
                >{helperText}</FormHelperText>
            )}
        </FormControl>
    );
});

TextField.displayName = 'TextField';
